<template>
<div class="lolita-page">
  <!-- 共用头部导航栏 -->
  <header-nav></header-nav>

  <!-- 3D 画布容器 -->
  <div class="stage3d" ref="stage3d">
    <canvas ref="glCanvas"></canvas>

    <!-- 加载遮罩 -->
    <div class="loading" v-if="loading">
      <div class="loading-ring"></div>
      <p class="loading-text">正在唤醒手办…</p>
      <p class="loading-pct">{{ progress }}%</p>
    </div>

    <!-- 标题 -->
    <div class="stage-title">
      <h1 class="title-cn">童 真 幻 想</h1>
      <p class="title-en">PURE FANTASY · FIGURE GALLERY</p>
    </div>

    <!-- 操作提示 -->
    <div class="hint">拖拽旋转 · 滚轮缩放</div>

    <!-- 底部光环装饰 -->
    <div class="floor-ring"></div>
  </div>
</div>
</template>

<script>
import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import HeaderNav from '../HeaderNav.vue'


export default {
  name: "Lolita-JK-HanFu",
  components: { HeaderNav },
  data() {
    return {
      loading: true,
      progress: 0,
      threeRef: null
    }
  },
  mounted() {
    this.$nextTick(() => this.initThree())
  },
  beforeDestroy() {
    if (this._resizeHandler) {
      window.removeEventListener('resize', this._resizeHandler)
      this._resizeHandler = null
    }
    if (this.threeRef) {
      cancelAnimationFrame(this.threeRef.raf)
      this.threeRef.controls.dispose()
      this.threeRef.renderer.dispose()
      this.threeRef = null
    }
  },
  methods: {
    initThree() {
      const container = this.$refs.stage3d
      const canvas = this.$refs.glCanvas
      const W = container.clientWidth
      const H = container.clientHeight

      // 场景
      const scene = new THREE.Scene()
      scene.fog = new THREE.FogExp2(0x1a0a28, 0.018)

      // 相机
      const camera = new THREE.PerspectiveCamera(45, W / H, 0.1, 100)
      camera.position.set(0, 2, 8)

      // 渲染器
      const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true })
      renderer.setSize(W, H)
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
      renderer.toneMapping = THREE.ACESFilmicToneMapping
      renderer.toneMappingExposure = 1.1
      renderer.outputColorSpace = THREE.SRGBColorSpace

      // 控制器
      const controls = new OrbitControls(camera, canvas)
      controls.enableDamping = true
      controls.dampingFactor = 0.06
      controls.minDistance = 4
      controls.maxDistance = 16
      controls.maxPolarAngle = Math.PI / 1.8
      controls.target.set(0, 1, 0)
      controls.autoRotate = true
      controls.autoRotateSpeed = 1.2

      /* ===== 灯光 ===== */
      // 环境光（粉紫）
      scene.add(new THREE.AmbientLight(0x8866aa, 1.2))

      // 主聚光灯（暖白偏粉）
      const spot = new THREE.SpotLight(0xffc8e6, 200, 40, Math.PI / 4, 0.3, 1.0)
      spot.position.set(4, 8, 5)
      spot.castShadow = true
      scene.add(spot)

      // 补光（冷蓝紫）
      const fill = new THREE.DirectionalLight(0x7c8cff, 1.2)
      fill.position.set(-5, 3, -4)
      scene.add(fill)

      // 轮廓光（粉）
      const rim = new THREE.PointLight(0xff66cc, 60, 25)
      rim.position.set(0, 3, -5)
      scene.add(rim)

      /* ===== 地台光环 ===== */
      const ringGeo = new THREE.RingGeometry(2.8, 3.0, 64)
      const ringMat = new THREE.MeshBasicMaterial({
        color: 0xff8ad4,
        transparent: true,
        opacity: 0.35,
        side: THREE.DoubleSide
      })
      const ring = new THREE.Mesh(ringGeo, ringMat)
      ring.rotation.x = -Math.PI / 2
      ring.position.y = -0.01
      scene.add(ring)

      const ring2Geo = new THREE.RingGeometry(3.3, 3.35, 64)
      const ring2Mat = new THREE.MeshBasicMaterial({
        color: 0xc084fc,
        transparent: true,
        opacity: 0.2,
        side: THREE.DoubleSide
      })
      const ring2 = new THREE.Mesh(ring2Geo, ring2Mat)
      ring2.rotation.x = -Math.PI / 2
      ring2.position.y = -0.01
      scene.add(ring2)

      /* ===== 加载模型 ===== */
      const loader = new GLTFLoader()
      loader.load(
        '/models/shouban.glb',
        (gltf) => {
          const model = gltf.scene
          // 自动居中并缩放到合适尺寸
          const box = new THREE.Box3().setFromObject(model)
          const size = box.getSize(new THREE.Vector3())
          const center = box.getCenter(new THREE.Vector3())
          const maxDim = Math.max(size.x, size.y, size.z)
          const scale = 3.5 / maxDim
          model.scale.setScalar(scale)
          model.position.x = -center.x * scale
          model.position.z = -center.z * scale
          model.position.y = -center.y * scale
          model.traverse((child) => {
            if (child.isMesh) {
              child.castShadow = true
              child.receiveShadow = true
            }
          })
          scene.add(model)
          this.loading = false
        },
        (xhr) => {
          if (xhr.total) {
            this.progress = Math.round((xhr.loaded / xhr.total) * 100)
          }
        },
        (err) => {
          console.error('模型加载失败', err)
          this.loading = false
        }
      )

      /* ===== 动画循环 ===== */
      const clock = new THREE.Clock()
      this.threeRef = { scene, camera, renderer, controls, raf: 0 }
      const self = this
      const animate = () => {
        const t = clock.getElapsedTime()
        ringMat.opacity = 0.25 + 0.15 * Math.sin(t * 2)
        ring2Mat.opacity = 0.15 + 0.1 * Math.sin(t * 2 + 1)
        ring.rotation.z = t * 0.1
        ring2.rotation.z = -t * 0.15
        controls.update()
        renderer.render(scene, camera)
        self.threeRef.raf = requestAnimationFrame(animate)
      }
      animate()

      // 自适应
      this._resizeHandler = () => {
        const w = container.clientWidth
        const h = container.clientHeight
        camera.aspect = w / h
        camera.updateProjectionMatrix()
        renderer.setSize(w, h)
      }
      window.addEventListener('resize', this._resizeHandler)
    }
  }
}
</script>

<style scoped>
.lolita-page {
  position: relative;
  width: 100%;
  height: 100vh;
  padding-top: 60px; /* 为固定头部导航栏让位 */
  box-sizing: border-box;
  overflow: hidden;
  background: radial-gradient(ellipse at 50% 40%, #2a1040 0%, #150825 50%, #0a0415 100%);
}

.stage3d {
  position: relative;
  width: 100%;
  height: 100%;
}

.stage3d canvas {
  display: block;
  width: 100% !important;
  height: 100% !important;
}

/* 加载遮罩 */
.loading {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(15, 5, 25, 0.85);
  z-index: 10;
}
.loading-ring {
  width: 50px;
  height: 50px;
  border: 2px solid rgba(255,150,220,0.2);
  border-top-color: #ff8ad4;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
.loading-text {
  color: #ffb8e6;
  font-size: 14px;
  letter-spacing: 3px;
  margin: 0;
}
.loading-pct {
  color: rgba(255,200,240,0.5);
  font-size: 12px;
  margin-top: 6px;
}

/* 标题 */
.stage-title {
  position: absolute;
  top: 30px;
  left: 50%;
  transform: translateX(-50%);
  text-align: center;
  z-index: 5;
  pointer-events: none;
}
.title-cn {
  font-size: 28px;
  font-weight: 300;
  letter-spacing: 12px;
  margin: 0;
  background: linear-gradient(135deg, #ffd6f0, #ff9ed8, #d4a5ff);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}
.title-en {
  font-size: 9px;
  letter-spacing: 5px;
  color: rgba(255,200,240,0.4);
  margin: 6px 0 0;
  font-family: Georgia, serif;
}

/* 提示 */
.hint {
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 11px;
  letter-spacing: 3px;
  color: rgba(255,200,240,0.4);
  z-index: 5;
  pointer-events: none;
}

/* 底部光环装饰 */
.floor-ring {
  position: absolute;
  bottom: 10%;
  left: 50%;
  transform: translateX(-50%);
  width: 400px;
  height: 60px;
  border-radius: 50%;
  background: radial-gradient(ellipse, rgba(255,130,215,0.12), transparent 70%);
  filter: blur(20px);
  z-index: 0;
  pointer-events: none;
}
</style>
